import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { sendAdvisorMessage } from '../../lib/api';

/**
 * AtreyaBubble — floating concierge for the Home page.
 *
 * A quiet circular FAB anchored bottom-right. Tap to slide open a drawer
 * with a chat surface that talks to `/api/advisor/chat`. The backend's
 * system prompt has been updated to give Atreya his identity and the
 * Seva Sutras (100 % Kewra base · 211 M $SEVA supply · 25 % markup ·
 * 1,000-Guardian Circle · refer-3-earn-100 SEVA · Recorded, not promised).
 */

const FIRST_TURN = {
  role: 'assistant',
  content:
    'Welcome to the Atelier of Kewra, Kamal, and Karma. How may I guide your ritual today?',
};

const PROMPTS = [
  'What is the 3K Protocol?',
  'Why is the base 100 % Kewra?',
  'How is $SEVA capped at 211 M?',
];

const AtreyaBubble = ({ testId = 'atreya-bubble' }) => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([FIRST_TURN]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Always pin the bottom of the chat as new content arrives
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async (text) => {
    const t = (text || '').trim();
    if (!t || sending) return;
    setMessages((m) => [...m, { role: 'user', content: t }]);
    setDraft('');
    setSending(true);
    try {
      const res = await sendAdvisorMessage({ session_id: sessionId, message: t });
      setSessionId(res.session_id);
      setMessages((m) => [...m, { role: 'assistant', content: res.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            'The house mailbox is briefly silent. Please try once more in a moment.',
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        type="button"
        data-testid={`${testId}-launcher`}
        aria-label="Speak with Atreya · the AI concierge"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 group inline-flex items-center gap-3 pl-3 pr-5 py-3 bg-[#7C9CB1] text-[#F4F8F9] shadow-2xl hover:bg-[#90B0BF] transition-colors duration-300"
      >
        <span className="w-9 h-9 rounded-full bg-[#F4F8F9]/15 flex items-center justify-center">
          {open ? <X size={16} strokeWidth={1.5} /> : <MessageCircle size={16} strokeWidth={1.5} />}
        </span>
        <span className="hidden sm:flex flex-col items-start leading-none">
          <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#F4F8F9]/75">
            Concierge
          </span>
          <span className="font-serif italic text-base">Atreya</span>
        </span>
      </button>

      {/* Slide-over drawer */}
      {open && (
        <aside
          data-testid={`${testId}-drawer`}
          role="dialog"
          aria-labelledby="atreya-drawer-title"
          className="fixed bottom-24 right-6 z-40 w-[92vw] sm:w-[420px] max-h-[78vh] flex flex-col bg-[#F4F8F9] border border-[#C1D7E2] shadow-2xl"
        >
          {/* Drawer head */}
          <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-[#C1D7E2]">
            <div>
              <p className="font-mono text-[9px] tracking-[0.32em] uppercase text-[#90B0BF]">
                The Purity Advisor · Vedic Ritual Optimizer
              </p>
              <h3
                id="atreya-drawer-title"
                className="mt-1 font-serif text-2xl text-[#7C9CB1] font-light leading-tight"
              >
                Atreya
              </h3>
              <p className="mt-1 font-serif italic text-[#90B0BF] text-sm leading-snug">
                Descendant of Atri · teacher of Ayurvedic medicine
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={`${testId}-close`}
              aria-label="Close"
              className="text-[#90B0BF] hover:text-[#7C9CB1]"
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          </header>

          {/* Conversation */}
          <div
            ref={scrollRef}
            data-testid={`${testId}-conversation`}
            className="flex-1 overflow-y-auto px-6 py-5 space-y-4"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`${testId}-msg-${m.role}`}
                className={`text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'ml-auto max-w-[85%] bg-[#7C9CB1] text-[#F4F8F9] px-4 py-3'
                    : 'max-w-[90%] text-[#7C9CB1]/95'
                }`}
              >
                {m.role === 'assistant' && (
                  <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#90B0BF] mb-1">
                    Atreya
                  </p>
                )}
                <p className={m.role === 'assistant' ? 'font-serif italic' : ''}>
                  {m.content}
                </p>
              </div>
            ))}
            {sending && (
              <div data-testid={`${testId}-typing`} className="max-w-[90%] text-[#90B0BF]/80 text-sm">
                <p className="font-mono text-[9px] tracking-[0.3em] uppercase mb-1">Atreya</p>
                <p className="flex items-center gap-2 font-serif italic">
                  <Loader2 size={12} className="animate-spin" /> consulting the Sutras…
                </p>
              </div>
            )}
          </div>

          {/* Quick prompts (only on the very first turn) */}
          {messages.length === 1 && (
            <div className="px-6 pb-3 flex flex-wrap gap-2">
              {PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => send(p)}
                  data-testid={`${testId}-prompt`}
                  className="text-[10px] tracking-[0.18em] uppercase border border-[#C1D7E2] text-[#7C9CB1] px-3 py-1.5 hover:bg-[#C1D7E2]/50 transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          )}

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(draft);
            }}
            className="flex items-end gap-2 px-6 py-4 border-t border-[#C1D7E2]"
          >
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(draft);
                }
              }}
              placeholder="A question for the house…"
              data-testid={`${testId}-input`}
              className="flex-1 resize-none bg-transparent text-[#7C9CB1] placeholder-[#90B0BF]/60 font-serif text-sm focus:outline-none border-b border-[#7C9CB1]/30 focus:border-[#7C9CB1] pb-1"
            />
            <button
              type="submit"
              disabled={sending || !draft.trim()}
              data-testid={`${testId}-send`}
              className="p-2 text-[#7C9CB1] hover:text-[#90B0BF] disabled:opacity-40"
              aria-label="Send"
            >
              <Send size={14} strokeWidth={1.6} />
            </button>
          </form>
        </aside>
      )}
    </>
  );
};

export default AtreyaBubble;
